import { useEffect, useMemo, useState } from "react";
import { ArrowUp, Volume2, X } from "lucide-react";

import { useOrganisation } from "@/api/organisation-api";
import { CaseRail } from "./CaseRail";
import { CaseReviewer } from "./CaseReviewer";
import { PastRoundReviewer } from "./PastRoundReviewer";
import { CandidateApplicationForm } from "@/components/tests/CandidateApplicationForm";
import {
  useAttemptApplication,
  useCandidateApplication,
} from "@/api/application-api";
import { RecordingsStepper } from "./RecordingsStepper";
import { ScreeningReview } from "./ScreeningReview";
import { useAttemptReview, useScreeningRecording } from "@/api/tests-api";
import {
  usePanelBoard,
  useSavePanelSheet,
  type PanelSheet,
} from "@/api/panel-api";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth-store";
import { formatDateDMY } from "@/utils/dateFormat";

interface PanelConsoleProps {
  candidateId: string;
  candidateName?: string | null;
  /**
   * The ROUND being scored: one exam given to this candidate. A panel
   * sheet is keyed on it, and writes are refused without it.
   */
  assignmentId?: string;
  /**
   * The sitting whose recordings this screening is about.
   *
   * Optional because the sheet is keyed on the CANDIDATE, not the
   * attempt -- a panel scores a person once, however many times they
   * sat. Given, the recordings are walked here; omitted, the console is
   * still the whole sheet.
   */
  attemptId?: string;
  /** The round's exam. The reference's console types a "Position"
   *  here; the exam is the thing we actually hold. */
  examTitle?: string | null;
  onClose: () => void;
}

/**
 * The reviewer's screen.
 *
 * THE SCREENING SHEET WAS REMOVED FROM IT on 2026-08-28, on
 * instruction. Stages 00-09, the red flags and the declaration are no
 * longer rendered: the reviewer reads the candidate's step one, listens
 * to the dictations, and writes a comment per case. Nothing is scored.
 *
 * NOTHING WAS DELETED BEHIND THAT. ``panel.py``, ``panel_template.py``,
 * the ``panel_assessments`` table and its routes are all still live and
 * still tested -- so every screening already written is still there and
 * still correct, and bringing the console back would be re-rendering a
 * model that never stopped working. CLAUDE.md carries the restore
 * point: the branch and the last commit in which the sheet rendered.
 *
 * The sheet is still what OWNS a round: the first reviewer to save
 * anything -- a comment, or marking the review complete -- claims it,
 * and everyone else reads it. ``can_edit`` comes from the SERVER.
 *
 * WHAT REMAINS OF THE OLD DOCSTRING, because it still applies:
 *
 * IT DECIDES NOTHING. Every threshold, every gate and the recommendation
 * itself come from the server and are rendered here as-is. The reference
 * design computed the verdict in the browser, which makes each panel
 * member's laptop an authority on a hiring decision — two people looking
 * at the same candidate could be shown different answers, and neither
 * survived a refresh.
 *
 * ONE SCREENING PER ROUND, changed 2026-08-28 on instruction. It used
 * to be one sheet per panel member -- everyone read everyone's, nobody
 * could edit anybody else's, and the argument was that a panel works
 * because the views are formed independently. The practice screens with
 * ONE radiologist, under which a second sheet is not a second opinion
 * but two people unknowingly scoring the same candidate.
 *
 * The first reviewer to SAVE owns the round; opening it to read does
 * not, because a sheet is only created on write. Everyone else gets it
 * read-only with the owner named. ``can_edit`` comes from the SERVER,
 * for the same reason the verdict does.
 *
 * SAVES ARE PER STAGE, and the server merges them stage-by-stage and
 * item-by-item. A panel scores over an afternoon, so a console that sent
 * the whole sheet on every change would overwrite its own earlier stages
 * from whatever state it happened to be holding. That bug was real —
 * found by driving the API — and the merge is the fix, which is why this
 * component sends only what changed and never tracks dirtiness itself.
 */
export function PanelConsole({
  candidateId,
  candidateName,
  attemptId,
  examTitle,
  assignmentId,
  onClose,
}: PanelConsoleProps) {
  const { toast } = useToast();
  const board = usePanelBoard(candidateId, assignmentId);
  // The practice's own name, never the reference's. Same source and
  // fallback as the candidate's form.
  const org = useOrganisation();
  const practice = org.data?.name?.trim() || "The practice";
  const save = useSavePanelSheet(candidateId, assignmentId);
  // The dictations and the comments on them. The stepper fetches the
  // same query; React Query hands both the one response.
  const review = useAttemptReview(attemptId ?? null);
  // Asked for every sitting, cheaply: a reporting exam answers with no
  // url and the console renders exactly what it did before.
  const screening = useScreeningRecording(attemptId ?? null);
  const answers = review.data?.answers ?? [];
  // WHAT THEY SAID WHEN THEY SAT THIS, not what they say today. The
  // attempt carries a frozen copy; the live row is the fallback for
  // sittings that predate snapshots, and there is deliberately no
  // backfill -- inventing one would assert that today's answers are
  // what was said then.
  const snapshot = useAttemptApplication(attemptId ?? null);
  const liveRow = useCandidateApplication(candidateId);
  const applicationQuery = attemptId ? snapshot : liveRow;
  const applicationRow = applicationQuery.data ?? null;

  const mine: PanelSheet | undefined = useMemo(
    () => board.data?.sheets.find((s) => s.mine),
    [board.data],
  );
  const others = useMemo(
    () => (board.data?.sheets ?? []).filter((s) => !s.mine),
    [board.data],
  );
  const template = board.data?.template;

  // ONE SCREENING PER ROUND, and the SERVER decides whose. Same reason
  // it decides the verdict: two people opening the same round must not
  // be told different things by their own browsers. The claim is taken
  // on the first write, so reading somebody's screening never locks it.
  const canEdit = board.data?.can_edit ?? true;
  // Which case the full-screen viewer is open on, or null for closed.
  // It lives HERE rather than in the stepper because two things open it
  // now -- the stepper's own button and the case rail -- and a viewer
  // mounted by each would be two viewers disagreeing about which case
  // is open.
  const [viewerAt, setViewerAt] = useState<number | null>(null);
  // The case rail, off until asked for. Off rather than on because the
  // application is what the screen is for: a list that appears beside
  // it uninvited is furniture, one the reviewer opened is a tool.
  const [railOpen, setRailOpen] = useState(false);
  // Whether the reader has gone far enough down to want a way back.
  // 300px rather than 0: an arrow that appears the moment anything
  // moves is a flicker, not an affordance.
  const [scrolled, setScrolled] = useState(false);

  // An earlier sitting, opened to read rather than merely listed.
  const [pastRound, setPastRound] = useState<{
    attemptId: string;
    title: string;
  } | null>(null);
  const screenedBy = board.data?.screened_by_name ?? null;

  // Everyone holding a sheet in THIS round, the signed-in reviewer
  // included even before they have scored anything -- otherwise the
  // panel's own name is missing from the sheet they are filling in.
  const me = useAuthStore((st) => st.user);
  const assessors = useMemo(() => {
    const names = (board.data?.sheets ?? [])
      .map((sh) => sh.reviewer_name)
      .filter((n): n is string => Boolean(n));
    const own = me?.full_name ?? me?.username ?? null;
    if (own && !names.includes(own)) names.push(own);
    return names.length > 0 ? names.join(", ") : "—";
  }, [board.data?.sheets, me]);

  // When this round was scored, not today's date: a sheet reopened in
  // November must not restamp itself with November.
  const screeningDate =
    formatDateDMY(mine?.submitted_at ?? mine?.updated_at ?? null) ||
    formatDateDMY(new Date().toISOString());

  // A plain-text record of what the review now IS: who read it, when,
  // and what they said about each dictation. It used to walk the
  // template stage by stage; there are no scores to walk.
  const copySummary = async () => {
    const L: string[] = [
      "IMG RADIOLOGIST - REVIEW RECORD",
      `Candidate: ${board.data?.candidate_name ?? candidateName ?? "-"}   Exam: ${examTitle ?? "-"}`,
      `Reviewer: ${assessors}   Date: ${screeningDate}`,
      "",
      "The candidate's application and their dictations were read on",
      "screen; the comments below are what the reviewer recorded.",
      "",
    ];
    for (const a of answers) {
      L.push(`[${a.position + 1}] ${a.title}${a.has_audio ? "" : "  (no recording)"}`);
      const notes = a.reviews.filter((r) => r.comments);
      if (notes.length === 0) L.push("    No comment recorded.");
      for (const r of notes) L.push(`    ${r.reviewer_name ?? "Reviewer"}: ${r.comments}`);
      L.push("");
    }
    try {
      await navigator.clipboard.writeText(L.join("\n"));
      toast({ title: "Summary copied to clipboard" });
    } catch {
      toast({ title: "Could not copy the summary", variant: "error" });
    }
  };

  /**
   * Print the review.
   *
   * The console is a fixed, scrolling overlay nested deep in the admin
   * page, so a print stylesheet alone cannot say "print this and
   * nothing else". Hiding the rest with ``visibility: hidden`` was the
   * first try and it printed two blank pages -- invisible content is
   * still laid out and still paginates.
   *
   * So the path from <body> down to the console is walked and only the
   * SIBLINGS at each level are marked. The ancestor chain survives, and
   * no hidden box is left to fill a page. Undone on afterprint, and on
   * a timer for the browsers that never fire it.
   */
  const printSheet = () => {
    const root = document.querySelector<HTMLElement>(
      '[data-cy="panel-console"]',
    );
    if (!root) {
      window.print();
      return;
    }
    const hidden: HTMLElement[] = [];
    const ancestors: HTMLElement[] = [];
    for (
      let node: HTMLElement | null = root;
      node && node !== document.body;
      node = node.parentElement
    ) {
      for (const sibling of Array.from(node.parentElement?.children ?? [])) {
        if (sibling !== node && sibling instanceof HTMLElement) {
          sibling.classList.add("sk-print-hide");
          hidden.push(sibling);
        }
      }
      // THE ANCESTORS ARE KEPT AND MUST ALSO BE UNDONE. Two of them --
      // the admin shell's scrolling body and the section clipping it --
      // carry overflow, and an overflow container prints its SCROLLBAR:
      // a grey track down the right of every page, inside the margin,
      // which reads as part of the record. Hiding them is not an option,
      // the console is inside them; they are flattened instead.
      if (node !== root) {
        node.classList.add("sk-print-ancestor");
        ancestors.push(node);
      }
    }
    root.classList.add("sk-print-root");
    const collapsed = Array.from(
      root.querySelectorAll<HTMLDetailsElement>("details:not([open])"),
    );
    collapsed.forEach((d) => (d.open = true));

    let done = false;
    const restore = () => {
      if (done) return;
      done = true;
      hidden.forEach((el) => el.classList.remove("sk-print-hide"));
      ancestors.forEach((el) => el.classList.remove("sk-print-ancestor"));
      root.classList.remove("sk-print-root");
      collapsed.forEach((d) => (d.open = false));
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.setTimeout(restore, 60_000);
    window.print();
  };

  const backToTop = () => {
    document
      .querySelector<HTMLElement>('[data-cy="panel-console"] > div')
      ?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Close on Escape, like the review drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const push = (patch: Parameters<typeof save.mutate>[0]) =>
    save.mutate(patch, {
      onError: () =>
        toast({ title: "Could not save that change", variant: "error" }),
    });

  if (board.isLoading) {
    return (
      <Shell onClose={onClose} title={candidateName ?? "Screening"}>
        <p className="text-sm text-foreground-muted">Loading the sheet…</p>
      </Shell>
    );
  }
  if (!template) {
    return (
      <Shell onClose={onClose} title={candidateName ?? "Screening"}>
        <p className="text-sm text-status-urgent">
          Could not load the screening template.
        </p>
      </Shell>
    );
  }

  return (
    <Shell
      onClose={onClose}
      eyebrow={`${practice} · Candidate review`}
      title="Candidate review"
      lede={`Read what ${
        board.data?.candidate_name ?? candidateName ?? "the candidate"
      } said in step one, then listen to their dictations. Nothing here is scored — record what you make of each dictation as you go.`}
      // The state is SAID, not left to the button's label: opening a
      // review somebody else finished would otherwise greet you with
      // "Reopen" and nothing to explain it. The template version has
      // gone with the sheet -- there is no template in play.
      onScroll={(top) => setScrolled(top > 300)}
      subtitle={
        mine?.submitted_at
          ? `Reviewed — ${formatDateDMY(mine.submitted_at)}`
          : "Not yet marked reviewed"
      }
    >
      {/* HALF THE HEIGHT IT WAS. The band carried the reference's
          readout beside these four values -- lamp, status line and a
          meter per scored stage. There is no verdict to report now that
          the reviewer scores nothing, and a readout saying ASSESSMENT
          IN PROGRESS for ever would be worse than none. What is left is
          who, which sitting, and when. */}
      <section className="sk-console sk-console-slim" data-cy="panel-band">
        {/* THE TOOLS ARE A COLUMN OF THE BAND, not a layer over it.
            Absolutely positioned they sat on top of the meta grid and
            covered the Date label -- reported, and true at every width
            because the grid's last column ends where the chips begin.
            As a column they take horizontal space, which the band has,
            instead of vertical space, which is what it was slimmed to
            save. */}
        <div className="sk-console-tools">
        {/* THE WAY OUT TRAVELS WITH THE BAND. The drawer's own close sits
              in the header, and a scored sheet is thousands of pixels
              long -- measured at the foot of one, the header button was
              7117px above the viewport, and the 98vw drawer leaves a 14px
              backdrop strip that nothing can reliably hit. That left
              Escape, which is not discoverable, and the browser's back
              button, which is not ours. The band is already sticky, so
              the exit rides along with it. */}
          <button
            type="button"
            className="sk-console-icon sk-console-close"
            data-cy="btn-close-panel-band"
            onClick={onClose}
            title="Close the review"
            aria-label="Close the review"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
          {/* THE DICTATIONS ARE A LONG WAY DOWN. Step one is nine
              sections and roughly two screens of reading, so a reviewer
              who wants to hear a case first had to scroll past all of it.

              IT DOES TWO DIFFERENT THINGS BY WIDTH, on purpose. Wide
              enough for the rail and it opens the case list beside the
              drawer, where it stays put while the application scrolls
              past -- the cases are then reachable without leaving the
              answers at all. Too narrow for the rail and there is
              nowhere to put a list that is not on top of the reading, so
              it falls back to scrolling to the dictations, which is what
              this button did before the rail existed. */}
          <button
            type="button"
            data-cy="btn-jump-dictations"
            className={`sk-console-icon sk-console-jump${railOpen ? " is-on" : ""}`}
            title="Their dictations"
            aria-label="Their dictations"
            aria-pressed={railOpen}
            onClick={() => {
              if (window.matchMedia("(min-width: 1320px)").matches) {
                setRailOpen((on) => !on);
                return;
              }
              // MEASURED AT CLICK TIME, not a fixed scroll-margin. The
              // band is what the heading has to clear, and its height
              // depends on the layout: ~85px in one row on a wide screen,
              // ~150 stacked below 900px. A constant cleared one of them
              // and buried the heading under the band on the other.
              const heading = document.getElementById("review-dictations");
              const scroller = document.querySelector<HTMLElement>(
                '[data-cy="panel-console"] > div',
              );
              if (!heading || !scroller) return;
              const band = document.querySelector(".sk-console");
              const scrollerTop = scroller.getBoundingClientRect().top;
              // WHERE THE BAND WILL BE ONCE STUCK, not where it is now.
              // Measuring its current bottom under-scrolled by 584px,
              // because at rest the band sits below the mast and only
              // rises to its sticky offset once the page moves. So the
              // space to clear is its own height plus the offset it
              // sticks at plus the scroller's padding -- all read, none
              // assumed.
              const clears = band
                ? band.getBoundingClientRect().height +
                  parseFloat(getComputedStyle(band).top || "0") +
                  parseFloat(getComputedStyle(scroller).paddingTop || "0")
                : 0;
              const delta = heading.getBoundingClientRect().top - scrollerTop;
              scroller.scrollTo({
                top: scroller.scrollTop + delta - clears - 16,
                behavior: "smooth",
              });
            }}
          >
            <Volume2 aria-hidden className="h-5 w-5" />
          </button>
        </div>

        <div className="sk-meta-grid">
          <MetaField label="Candidate">
            {board.data?.candidate_name ?? candidateName ?? "—"}
          </MetaField>
          <MetaField label="Exam">{examTitle ?? "—"}</MetaField>
          <MetaField label="Assessor(s)">{assessors}</MetaField>
          <MetaField label="Date">{screeningDate}</MetaField>
        </div>

      </section>

      {/* WHAT THE CANDIDATE DID, IN THE ORDER THEY DID IT, and it is
          now the whole screen rather than a preamble to a scoring
          sheet. The reviewer reads step one, listens to step two, and
          writes a comment per dictation. There is nothing to score.

          THE APPLICATION IS RENDERED BY THE CANDIDATE'S OWN COMPONENT,
          read-only, with their answers in it -- same labels, same
          options, same help lines, because it IS the same component.
          A second renderer would be a second set of questions to keep
          in step, and this repo has already shipped a form that looked
          right and asked different ones. */}
      <h3 className="mt-6 text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-hover">
        Step 1 of 2 · Their application
      </h3>
      {/* NO CARD AROUND IT. The form is made of .sk-card sections; a
          card wrapping them framed a frame and pushed the answers in by
          another 16px on each side. */}
      <div className="mt-2" data-cy="review-application-card">
        {applicationRow ? (
          <CandidateApplicationForm answers={applicationRow} />
        ) : applicationQuery.isLoading ? (
          <p className="sk-help">Loading their application…</p>
        ) : (
          <p className="sk-help">This candidate has not applied yet.</p>
        )}
      </div>

      <h3
        id="review-dictations"
        className="mt-6 text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-hover"
      >
        Step 2 of 2 · Their dictations
      </h3>
      {attemptId && (
        <div className="mt-2">
          {/* A VIDEO ROUND HAS NO PER-CASE DICTATIONS to step through:
              the whole sitting is one recording, and what the assessor
              needs is that file with an index into it and somewhere to
              write while it plays. Decided by whether a recording was
              assembled rather than by the exam's kind -- the console is
              opened from a list of attempts, and an attempt knows what it
              produced without being asked what it was supposed to. */}
          {screening.data?.url ? (
            <ScreeningReview
              attemptId={attemptId}
              questions={(review.data?.questions ?? []).map((q) => ({
                id: q.id,
                prompt: q.prompt,
                config: q.config,
              }))}
            />
          ) : (
            <RecordingsStepper attemptId={attemptId} onOpenCase={setViewerAt} />
          )}
        </div>
      )}


      {/* EARLIER SCREENINGS, read-only.
          Shown alongside and never pre-filled into the sheet above: a
          candidate's answers move between rounds -- 25 studies a day
          becomes 40, a registration condition lifts -- so a panel
          forming a fresh view must not open onto last year's scores.
          The CHANGE is the signal; hiding the old verdict would hide it,
          and pre-filling it would anchor the new one. */}
      {(board.data?.history?.length ?? 0) > 0 && (
        <section
          data-cy="panel-history"
          className="mt-4 rounded-xl border border-border bg-background-card p-4"
        >
          <h3 className="text-sm font-semibold text-foreground">
            Earlier screenings
          </h3>
          <p className="mt-1 text-xs text-foreground-muted">
            What the panel concluded on previous rounds. Read-only — score
            this round on its own evidence, then compare.
          </p>
          <ul className="mt-3 flex flex-col gap-2">
            {(board.data?.history ?? []).map((round) => (
              <li
                key={round.test_assignment_id ?? "legacy"}
                data-cy="panel-history-round"
                className="rounded-lg border border-border bg-background p-3"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-foreground">
                    {round.definition_title ??
                      "Screened before rounds were recorded"}
                  </span>
                  <span className="text-[11px] tabular-nums text-foreground-muted">
                    {round.assigned_at
                      ? new Date(round.assigned_at).toLocaleDateString(
                          undefined,
                          { day: "numeric", month: "short", year: "numeric" },
                        )
                      : "date unknown"}
                  </span>
                </div>
                <ul className="mt-1.5 flex flex-col gap-1">
                  {round.sheets.map((sheet) => (
                    <li
                      key={sheet.id}
                      className="flex items-baseline justify-between gap-3 text-xs text-foreground-muted"
                    >
                      <span>{sheet.reviewer_name ?? "A reviewer"}</span>
                      <span className="text-right">
                        {sheet.submitted_at
                          ? `reviewed ${formatDateDMY(sheet.submitted_at)}`
                          : "opened, not marked complete"}
                      </span>
                    </li>
                  ))}
                </ul>
                {/* An earlier round is worth OPENING, not just listing.
                    What a panel wants from it is what the candidate said
                    and how they dictated then -- so it opens the same
                    two things this screen shows for the current one. */}
                {round.attempt_id && (
                  <button
                    type="button"
                    data-cy="btn-open-past-round"
                    className="sk-btn sk-sm mt-2"
                    onClick={() =>
                      setPastRound({
                        attemptId: round.attempt_id as string,
                        title:
                          round.definition_title ?? "An earlier sitting",
                      })
                    }
                  >
                    Open this sitting
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Everyone else's verdicts on THIS round, read-only. A panel
          compares views; hiding them would move that comparison into a
          corridor. */}
      {others.length > 0 && (
        <section className="mt-4 rounded-xl border border-border bg-background-card p-4">
          <h3 className="text-sm font-semibold text-foreground">
            Other panel members
          </h3>
          <ul className="mt-2 flex flex-col gap-1.5">
            {others.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between text-xs text-foreground-muted"
                data-cy="panel-other-sheet"
              >
                <span className="font-medium text-foreground">
                  {s.reviewer_name ?? "A panel member"}
                  {s.submitted_at ? (
                    <span className="sk-crit" title="They have marked their screening complete">
                      COMPLETE
                    </span>
                  ) : (
                    <span className="sk-help ml-2">still scoring</span>
                  )}
                </span>
                <span>{s.verdict?.headline ?? "—"}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!canEdit && screenedBy && (
        <p
          data-cy="panel-readonly-banner"
          className="mt-4 rounded-lg border border-status-wip/40 bg-status-wip-bg px-3 py-2 text-sm text-status-wip"
        >
          <strong>Reviewed by {screenedBy}.</strong> This screening
          belongs to them and is read-only for you — everything below can
          be read, listened to and printed, but not scored.
        </p>
      )}

      {/* A DISABLED FIELDSET rather than a `disabled` prop on each
          control. Every scale button, tick, volume box, note and red-flag
          switch below it is disabled by the browser itself, so a control
          added later cannot be forgotten. It wraps only the scoring --
          the stepper's Previous/Next, the players and the application
          stay usable, because reading somebody else's screening is the
          whole point of loading it. */}

      {/* BACK TO THE TOP, once there is a top to go back to. The
          application runs to nine sections, so the way out and the band
          are a long way up from the foot of it -- and the band's own
          jump only goes the other way. */}
      {scrolled && (
        <button
          type="button"
          data-cy="btn-back-to-top"
          onClick={backToTop}
          title="Back to the top"
          aria-label="Back to the top"
          className="fixed bottom-6 right-8 z-[55] flex h-10 w-10 items-center justify-center rounded-full border border-border-strong bg-background-card text-foreground shadow-theme-md hover:bg-table-row-hover"
        >
          <ArrowUp aria-hidden className="h-4 w-4" />
        </button>
      )}

      {railOpen && answers.length > 0 && (
        <CaseRail
          answers={answers}
          onOpenCase={setViewerAt}
          onClose={() => setRailOpen(false)}
        />
      )}

      {viewerAt !== null && attemptId && (
        <CaseReviewer
          answers={answers}
          initialIndex={viewerAt}
          attemptId={attemptId}
          canEdit={canEdit}
          onClose={() => setViewerAt(null)}
        />
      )}

      {pastRound && (
        <PastRoundReviewer
          attemptId={pastRound.attemptId}
          candidateId={candidateId}
          title={pastRound.title}
          onClose={() => setPastRound(null)}
        />
      )}

      {/* The reference's submit bar, minus two of its three buttons.
          "Save / print record" and "Reset console" belong to a console
          that held everything in memory; this sheet is on the server,
          and a reset would destroy a verdict somebody may have acted
          on. Copying a summary out has no such counterpart. */}
      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        {mine?.submitted_at && (
          <span className="sk-help mr-auto" data-cy="panel-complete-state">
            Review marked complete on {formatDateDMY(mine.submitted_at)}. It
            stays editable — anything you change is saved and this date moves.
          </span>
        )}
        <button
          type="button"
          className="sk-btn"
          data-cy="btn-print-sheet"
          onClick={printSheet}
        >
          Print
        </button>
        <button
          type="button"
          className="sk-btn"
          data-cy="btn-copy-summary"
          onClick={copySummary}
        >
          Copy summary
        </button>
        {canEdit && (
          <button
            type="button"
            className={`sk-btn ${mine?.submitted_at ? "" : "sk-btn-primary"}`}
            data-cy="btn-complete-screening"
            onClick={() => push({ submitted: !mine?.submitted_at })}
          >
            {mine?.submitted_at ? "Reopen review" : "Mark review complete"}
          </button>
        )}
      </div>

      {/* The reference's use notes, and they are not boilerplate: two of
          the three are things a panel can get wrong in the room. */}
      <p className="sk-disclaimer">
        <strong>Use notes:</strong> Keep all test cases de-identified. Ask
        commitment questions about the role and location, not personal
        circumstances. RANZCR IMG pathways are being reformed — re-verify
        scope and conditions at offer stage. Everything here saves to the
        server as you go; there is nothing to lose by closing this.
      </p>
    </Shell>
  );
}

// ---------------------------------------------------------------------

function Shell({
  title,
  subtitle,
  eyebrow,
  lede,
  onClose,
  onScroll,
  children,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  lede?: string;
  onClose: () => void;
  /** The drawer IS the scroller -- this is an overlay, so window scroll
   *  never moves and a listener on it would never fire. Reported from
   *  here rather than found with a querySelector after render, which
   *  raced the first paint and silently attached to nothing. */
  onScroll?: (top: number) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      data-cy="panel-console"
      onClick={onClose}
    >
      {/* 960, NOT 98vw. The drawer was widened to almost the whole
          screen for the screening sheet -- a two-column band and nine
          stages of scoring wanted the room. The sheet has gone and the
          candidate's application is the whole page now, so the drawer
          hugs the 880 column it was written for instead of framing it
          with 340px of empty drawer on each side. */}
      <div
        className="flex h-full w-full max-w-[960px] flex-col overflow-y-auto bg-background px-5 pb-5 shadow-theme-lg"
        onClick={(e) => e.stopPropagation()}
        onScroll={(e) => onScroll?.(e.currentTarget.scrollTop)}
      >
        {/* THE SAME COLUMN THE CANDIDATE HAD: 880px, the reference's
            .wrap.narrow. It was 1240 -- the console's own .wrap -- back
            when a screening sheet lived here and wanted the room. The
            sheet has gone and the candidate's application is the whole
            page now, so it reads at the width it was written for: 880
            outer, 822.4 inside a card. At 1240 the form sat centred
            with 180px of nothing either side of it. */}
        <div className="mx-auto w-full max-w-[880px]">
        <header className="sk-mast mt-5 flex items-start justify-between gap-4">
          <div>
            {eyebrow && <p className="sk-eyebrow">{eyebrow}</p>}
            <h2 className="sk-mast-title">{title}</h2>
            {lede && <p className="sk-mast-lede">{lede}</p>}
            {subtitle && (
              <p className="mt-2 text-xs text-foreground-muted">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            data-cy="btn-close-panel"
            onClick={onClose}
            aria-label="Close the screening console"
            className="rounded-md border border-border p-1.5 text-foreground-muted hover:text-foreground"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </header>
        <div className="mt-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function MetaField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="sk-mfield">
      <span className="sk-mfield-label">{label}</span>
      <span className="sk-mvalue">{children}</span>
    </div>
  );
}

/** Short names for the meters. The reference takes the first word of
 *  the title; these are chosen instead, because "Motivation," and
 *  "Situational" are not what a panel member is looking for. */
